import type { SearchPayload } from '@civilgrid/ui/hooks';
import type {
  IntelligenceSummary,
  IntelligenceV2Item,
  Project,
  ProjectConflict,
  ProjectConflictAgency,
} from '~/common/types';
import { getAxiosClient } from './common';
import { transformProject } from './projects2';

const axios = getAxiosClient();

export type ProjectsApiResponse = {
  projects: Project[];
  count: number;
};

/** Search payload extended with map-list bookmark filters and geometry filter (backend-supported). */
export type ProjectsSearchPayload = SearchPayload & {
  bookmarkedOnly?: boolean;
  excludeBookmarked?: boolean;
  /** When set, only return projects whose geometry intersects this MultiPolygon (map viewport). */
  geometryFilter?: GeoJSON.MultiPolygon;
};

/** Payload for bulk update (only allowed fields). */
export type BulkUpdateProjectsPayload = {
  projectManagerId?: string;
  startDate?: string;
  endDate?: string;
  constructionStartDate?: string;
  constructionEndDate?: string;
};

/** Selection payload for bulk-update: when mode is 'all', search must be inside selection. */
export type BulkUpdateSelection =
  | { mode: 'partial'; selectedIds: string[] }
  | { mode: 'all'; excludedIds: string[]; search: ProjectsSearchPayload };

/** Body for POST /v3/projects/bulkUpdate. When selection.mode is 'all', selection.search is required. */
export type BulkUpdateProjectsBody = {
  organizationId: string;
  selection: BulkUpdateSelection;
  updates: BulkUpdateProjectsPayload;
};

export type BulkUpdateProjectsResponse = { updatedCount: number };

/** Body for POST /v3/projects/bulkBookmark. When selection.mode is 'all', selection.search is required. */
export type BulkAddProjectBookmarksBody = {
  organizationId: string;
  selection: BulkUpdateSelection;
};

export type BulkAddProjectBookmarksResponse = { addedCount: number };

/** Body for POST /v3/projects/bulkInvite. When selection.mode is 'all', selection.search is required. */
export type BulkInviteMembersBody = {
  organizationId: string;
  selection: BulkUpdateSelection;
  userIds?: string[];
  memberData?: { email: string; role: string; name?: string }[];
};

export type BulkInviteMembersResponse = { addedCount: number };

export type InviteMembersBody = {
  organizationId: string;
  projectId?: string;
  userIds?: string[];
  memberData?: { email: string; role: string; name?: string }[];
};

export type InviteMembersResponse = {
  addedCount: number;
  processedEmails?: string[];
  skippedEmails?: { email: string; reason: string }[];
};

/**
 * Toggle the current user's bookmark for a project.
 * Uses PUT /v3/projects/:projectId/bookmark.
 */
export async function updateProjectBookmark(projectId: string) {
  const response = await axios({
    method: 'PUT',
    url: `/v3/projects/${projectId}/bookmark`,
  });
  return response;
}

/**
 * Fetch projects for the current organization with optional search payload.
 * Uses /v3/projects; backend honors pagination, sort, filters, globalFilter,
 * and optional bookmarkedOnly / excludeBookmarked for map list sections.
 */
export async function fetchProjects(
  organizationId: string,
  searchPayload: ProjectsSearchPayload,
  { signal }: { signal?: AbortSignal } = {},
): Promise<ProjectsApiResponse> {
  const params = new URLSearchParams({
    organizationId,
    search: JSON.stringify(searchPayload),
  });

  const { data } = await axios({
    method: 'GET',
    url: `/v3/projects?${params.toString()}`,
    signal,
  });

  const projects = (data.projects ?? []).map((p: unknown) => transformProject(p));
  const count = typeof data.count === 'number' ? data.count : projects.length;
  return { projects, count };
}

/**
 * Fetch all projects matching filters with no pagination (map polygon data).
 * Uses GET /v3/projects/allProjects; same filters/globalFilter as list, returns all matching rows.
 */
export async function fetchAllProjects(
  organizationId: string,
  searchPayload: ProjectsSearchPayload,
  { signal, compact = true }: { signal?: AbortSignal; compact?: boolean } = {},
): Promise<ProjectsApiResponse> {
  const params = new URLSearchParams({
    organizationId,
    search: JSON.stringify(searchPayload),
  });
  if (compact) params.set('compact', 'true');

  const { data } = await axios({
    method: 'GET',
    url: `/v3/projects/allProjects?${params.toString()}`,
    signal,
  });

  const projects = (data.projects ?? []).map((p: unknown) => transformProject(p));
  const count = typeof data.count === 'number' ? data.count : projects.length;
  return { projects, count };
}

/**
 * Fetch the org's sample (sandbox) project. Returns null when org is Sandbox plan or no sandbox project exists.
 * Uses GET /v3/projects/sandbox?organizationId=...
 */
export async function fetchSandboxProject(
  organizationId: string,
  { signal }: { signal?: AbortSignal } = {},
): Promise<Project | null> {
  try {
    const params = new URLSearchParams({ organizationId });
    const { data } = await axios({
      method: 'GET',
      url: `/v3/projects/sandbox?${params.toString()}`,
      signal,
    });
    return data ? transformProject(data) : null;
  } catch {
    return null;
  }
}

/**
 * Bulk update projects by selection (partial ids or all matching search with optional exclusions).
 * When selection.mode is 'all', pass search so the backend applies the same filters.
 * organizationId is sent as a query param so org auth middleware can authorize the request.
 */
export async function bulkUpdateProjects(
  body: BulkUpdateProjectsBody,
  { signal }: { signal?: AbortSignal } = {},
): Promise<BulkUpdateProjectsResponse> {
  const params = new URLSearchParams({ organizationId: body.organizationId });
  const { data } = await axios({
    method: 'POST',
    url: `/v3/projects/bulkUpdate?${params.toString()}`,
    data: body,
    signal,
  });
  return data as BulkUpdateProjectsResponse;
}

/**
 * Bulk add projects to the current user's bookmarks by selection.
 * When selection.mode is 'all', pass search so the backend applies the same filters.
 */
export async function bulkAddProjectBookmarks(
  body: BulkAddProjectBookmarksBody,
  { signal }: { signal?: AbortSignal } = {},
): Promise<BulkAddProjectBookmarksResponse> {
  const params = new URLSearchParams({ organizationId: body.organizationId });
  const { data } = await axios({
    method: 'POST',
    url: `/v3/projects/bulkBookmark?${params.toString()}`,
    data: body,
    signal,
  });
  return data as BulkAddProjectBookmarksResponse;
}

/**
 * Bulk invite users to projects by selection. Backend adds each user only to projects
 * they are not already in. When selection.mode is 'all', pass search so the backend applies the same filters.
 */
export async function bulkInviteMembers(
  body: BulkInviteMembersBody,
  { signal }: { signal?: AbortSignal } = {},
): Promise<BulkInviteMembersResponse> {
  const params = new URLSearchParams({ organizationId: body.organizationId });
  const { data } = await axios({
    method: 'POST',
    url: `/v3/projects/bulkInvite?${params.toString()}`,
    data: body,
    signal,
  });
  return data as BulkInviteMembersResponse;
}

/**
 * Export projects to Excel. Uses same search payload as fetchProjects (filters, sort, etc.).
 * Returns raw xlsx buffer; caller should trigger download (e.g. downloadExcelFileBuffer).
 */
export async function exportProjectsToExcel(
  organizationId: string,
  payload: { columnDefs: { id: string; header: string }[]; search: ProjectsSearchPayload },
  { signal }: { signal?: AbortSignal } = {},
): Promise<ArrayBuffer> {
  const params = new URLSearchParams({ organizationId });
  const { data } = await axios({
    method: 'POST',
    url: `/v3/projects/export/xlsx?${params.toString()}`,
    data: payload,
    responseType: 'arraybuffer',
    signal,
  });
  return data as ArrayBuffer;
}

/**
 * Invite/add members via v3. Used by InviteTeamProjectDialog (single project) and InviteTeamOrgDialog (org-only).
 * - memberData: invite by email (add to org and optionally to project), send org invite email.
 * - userIds: add existing org members to project, send project invite email.
 */
export async function inviteMembers(
  body: InviteMembersBody,
  { signal }: { signal?: AbortSignal } = {},
): Promise<InviteMembersResponse> {
  const params = new URLSearchParams({ organizationId: body.organizationId });
  const { data } = await axios({
    method: 'POST',
    url: `/v3/projects/inviteMembers?${params.toString()}`,
    data: body,
    signal,
  });
  return data as InviteMembersResponse;
}

/** JSON payload for GET /v3/projects/:projectId/intelligence. */
export interface ProjectIntelligenceResponse {
  projectConflicts: ProjectConflict[];
  intelligenceSummary: IntelligenceSummary | null;
  conflictAgencies: ProjectConflictAgency[];
  intelligenceItems: Record<string, IntelligenceV2Item[]>;
}

/**
 * Fetch the redesigned Intelligence tab payload for a project.
 * Uses GET /v3/projects/:projectId/intelligence.
 */
export async function fetchProjectIntelligence(
  projectId: string,
  { signal }: { signal?: AbortSignal } = {},
): Promise<ProjectIntelligenceResponse> {
  const { data } = await axios({
    method: 'GET',
    url: `/v3/projects/${projectId}/intelligence`,
    signal,
  });
  return data as ProjectIntelligenceResponse;
}
